import { getUserData } from "@/auth/getUserData";
// @ts-ignore
import useUserSettings from "@/composables/useUserSettings";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useMultiSelect from "@/composables/v2.1/useMultiSelect";
import TeamInterface from "@/interfaces/team.interface";
import { useCustomersStore } from "@/store/useCustomersStore";
import { ActiveFilter, StatusFilterNameType, FilterKey, FilterType } from "@/types/filter.types";
import { InboxModuleType } from "@/types/inboxModule.type";
import { computed, ComputedRef, readonly, Ref, reactive, ref, UnwrapRef } from "@vue/composition-api";
import { useTeamsStore } from "@/store/useTeamsStore";
import { SortingType } from "@/types/sorting.type";
import { ChatListMode } from "@saysimple-platform/dtos";

// @NOTICE Abstract only!

export default function useFilters(
    state: UnwrapRef<FilterType>,
    inboxModuleType: InboxModuleType,
    searchString: Ref<string>,
    page: Ref<number>,
    activeFilter: Ref<ActiveFilter>,
    sortBy: Ref<ChatListMode>
) {
    const { getUserSetting } = useUserSettings();
    const teamsStore = useTeamsStore();

    const smartInboxAvailable = ref(true);
    // @ts-ignore
    const sortingOptions: ChatListMode[] = ["newestEntry", "oldestEntry", "longestWaiting", "shortestWaiting"];
    // @ts-ignore
    const enforcedTeamFilters: ComputedRef<{
        enabled: boolean;
        teams: TeamInterface[];
        teamIds: number[];
        includeUnassignedChats: boolean;
    }> = computed(() => {
        const includeUnassignedChats = getUserSetting("enforceUnassignedChats", "0") === "1";
        if (getUserSetting("enforceTeamFilter", "0") === "0") {
            return {
                enabled: false,
                includeUnassignedChats,
                teams: [],
                teamIds: [],
            };
        }

        const teams = teamsStore.teams.reduce((list: TeamInterface[], team: TeamInterface) => {
            if (team.teamUsers.some((teamUser) => teamUser.userId === getUserData().id)) {
                list.push(team);
            }
            return list;
        }, []);

        return {
            enabled: true,
            includeUnassignedChats,
            teams,
            teamIds: teams.map((team) => team.id),
        };
    });

    // @ts-ignore
    const setActiveFilter = (filter) => {
        smartInboxAvailable.value = filter.status?.name !== StatusFilterNameType.ARCHIVE;

        // If we like to change the filter to the same as we're already are, we don't have to switch.
        if (filter.keyword === activeFilter.value.keyword && filter.status?.name === activeFilter.value.status?.name) {
            return;
        }

        if (filter.status?.name === StatusFilterNameType.PERSONAL) {
            if (inboxModuleType === "email") {
                filter.status.query.assignedUser = getUserData().id;
            } else {
                filter.status.query.assignedUserId = getUserData().id;
            }
        }

        if (filter.status?.name === StatusFilterNameType.TEAM) {
            const { customerSettings } = useCustomersStore();
            if (inboxModuleType === "email") {
                filter.status.query = {
                    assignedTeam: {
                        $notnull: true,
                    },
                    status: "open",
                };

                if (!customerSettings.expandedTeamFilter) {
                    filter.status.query.assignedUser = {
                        $isnull: true,
                    };
                }
            } else {
                filter.status.query = {
                    assignedTeamId: {
                        $notnull: true,
                    },
                    status: "open",
                };

                if (!customerSettings.expandedTeamFilter) {
                    filter.status.query.assignedUserId = {
                        $isnull: true,
                    };
                }
            }
        }

        // For now, I've chosen to clear the multi select when switching the status filter to prevent unexpected behavior eventually.
        const { multiSelectEnabled, clearMultiSelect } = useMultiSelect();
        if (multiSelectEnabled.value) {
            clearMultiSelect();
        }

        activeFilter.value = {
            // @ts-ignore
            ...activeFilter.value,
            ...filter,
        };
    };

    const setFilterToLocalStorage = (name: FilterKey, value: unknown): void => {
        localStorage.setItem(`${inboxModuleType}.filter.${name}`, JSON.stringify(value));
    };

    const fetchFiltersFromLocalStorage = (): void => {
        const { isInboxModuleEnabled } = useInboxModuleType();
        if (!isInboxModuleEnabled(inboxModuleType)) {
            return;
        }

        state.teams = JSON.parse(localStorage.getItem(`${inboxModuleType}.filter.teams`) ?? "[]");
        state.channels = JSON.parse(localStorage.getItem(`${inboxModuleType}.filter.channels`) ?? "[]");
        state.tags = JSON.parse(localStorage.getItem(`${inboxModuleType}.filter.tags`) ?? "[]");
        state.labelIds = JSON.parse(localStorage.getItem(`${inboxModuleType}.filter.labelIds`) ?? "[]");
        state.userIds = JSON.parse(localStorage.getItem(`${inboxModuleType}.filter.userIds`) ?? "[]");
        state.read = JSON.parse(localStorage.getItem(`${inboxModuleType}.filter.read`) ?? "[]");
    };
    //@ts-ignore
    state.sortBy = JSON.parse(
        localStorage.getItem(`${inboxModuleType}.filter.sortBy`) ?? `"newestEntry"`
    ) as SortingType;
    sortBy.value = state.sortBy;

    return {
        getActiveFilter: readonly(activeFilter),
        filters: reactive(state),
        sortBy: sortBy,
        sortingOptions: readonly(sortingOptions),
        searchString,
        page,
        smartInboxAvailable, // todo remove?
        enforcedTeamFilters,

        setActiveFilter,
        fetchFiltersFromLocalStorage,
        setFilterToLocalStorage,
    };
}
